import React, { FC, useRef, useState } from "react";
import { Image, Tooltip } from "antd";
import {
  PlayCircleFilled,
  LinkOutlined,
  CloudUploadOutlined,
  PictureOutlined,
  FileOutlined,
  ExportOutlined,
  DownloadOutlined,
  AppstoreOutlined,
} from "@ant-design/icons";
import moment from "moment";
import { truncateString } from "utils/chatUtils";

const LINK_MIME_TYPE = "application/link";

type TabKey = "media" | "links";

const isLinkMessage = (message: MessageObj): boolean =>
  message?.data?.mimeType === LINK_MIME_TYPE;

const getMediaKind = (mimeType?: string): "image" | "video" | "file" => {
  if (!mimeType) return "file";
  if (mimeType.indexOf("image") !== -1) return "image";
  if (mimeType.indexOf("video") !== -1) return "video";
  return "file";
};

const getExt = (name?: string): string =>
  (name?.split(".").pop() || "").toLowerCase();

const getFileIcon = (name?: string): string => {
  const ext = getExt(name);
  return ext ? `/images/files-icon/${ext}.png` : "/images/files-icon/document.png";
};

const getDomain = (url?: string): string => {
  if (!url) return "";
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return url.replace(/^https?:\/\//, "").split("/")[0];
  }
};

const openInNewTab = (url?: string) => {
  if (url) window.open(url, "_blank", "noopener,noreferrer");
};

const MediaGalleryItem: FC<{ message: MessageObj }> = ({ message }) => {
  const url = message?.data?.urls?.original?.url;
  const name = message?.data?.originalName || message?.message || "Untitled";
  const kind = getMediaKind(message?.data?.mimeType);
  const time = message.originalCreatedAt || message.createdAt;
  const ext = getExt(name);

  return (
    <div className={`mg-tile mg-tile--${kind}`}>
      <div className="mg-tile__thumb">
        {kind === "image" && (
          <Image
            rootClassName="mg-tile__img"
            src={url}
            width="100%"
            height="100%"
          />
        )}

        {kind === "video" && (
          <div
            className="mg-tile__media"
            role="button"
            tabIndex={0}
            onClick={() => openInNewTab(url)}
          >
            <video src={url} muted preload="metadata" />
            <span className="mg-tile__play">
              <PlayCircleFilled />
            </span>
          </div>
        )}

        {kind === "file" && (
          <div
            className="mg-tile__media mg-tile__file"
            role="button"
            tabIndex={0}
            onClick={() => openInNewTab(url)}
          >
            <img
              src={getFileIcon(name)}
              onError={(e: any) => {
                e.target.src = "/images/files-icon/document.png";
              }}
              alt={name}
            />
            {ext && <span className="mg-tile__ext">{ext}</span>}
          </div>
        )}

        {/* type badge */}
        <span className="mg-tile__badge">
          {kind === "image" && <PictureOutlined />}
          {kind === "video" && <PlayCircleFilled />}
          {kind === "file" && <FileOutlined />}
        </span>

        {/* quick action */}
        {url && kind !== "image" && (
          <Tooltip title="Open">
            <button
              type="button"
              className="mg-tile__action"
              onClick={(e) => {
                e.stopPropagation();
                openInNewTab(url);
              }}
            >
              <DownloadOutlined />
            </button>
          </Tooltip>
        )}

        {/* hover caption */}
        <div className="mg-tile__caption">
          <span className="mg-tile__name" title={name}>
            {truncateString(name, 22)}
          </span>
          {time && (
            <span className="mg-tile__date">{moment(time).format("MMM D")}</span>
          )}
        </div>
      </div>
    </div>
  );
};

const LinkItem: FC<{ message: MessageObj }> = ({ message }) => {
  const url = message?.data?.urls?.original?.url || message?.message || "";
  const domain = getDomain(url);
  const title = message?.data?.originalName || message?.message || domain || url;
  const time = message.originalCreatedAt || message.createdAt;

  return (
    <a
      className="mg-link"
      href={url}
      target="_blank"
      rel="noopener noreferrer"
    >
      <span className="mg-link__icon">
        <LinkOutlined />
      </span>
      <span className="mg-link__body">
        <span className="mg-link__title" title={title}>
          {truncateString(title, 70)}
        </span>
        <span className="mg-link__meta">
          <span className="mg-link__domain" title={url}>
            {domain || url}
          </span>
          {time && (
            <>
              <span className="mg-link__dot">·</span>
              <span className="mg-link__date">
                {moment(time).format("MMM D, hh:mm a")}
              </span>
            </>
          )}
        </span>
      </span>
      <span className="mg-link__open">
        <ExportOutlined />
      </span>
    </a>
  );
};

const EmptyState: FC<{ icon: React.ReactNode; title: string; hint?: string }> = ({
  icon,
  title,
  hint,
}) => (
  <div className="mg-empty">
    <span className="mg-empty__icon">{icon}</span>
    <span className="mg-empty__title">{title}</span>
    {hint && <span className="mg-empty__hint">{hint}</span>}
  </div>
);

const MediaGallery: FC<{
  messages: MessageObj[];
  onFilesSelected?: (files: File[]) => void;
  canUpload?: boolean;
}> = ({ messages, onFilesSelected, canUpload }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [activeTab, setActiveTab] = useState<TabKey>("media");
  const [dragOver, setDragOver] = useState(false);

  const emitFiles = (fileList: FileList | null) => {
    if (fileList && fileList.length && onFilesSelected) {
      onFilesSelected(Array.from(fileList));
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    emitFiles(e.target.files);
    e.target.value = "";
  };

  const allMedia = (messages || []).filter((m) => m?.messageType === "media");
  const linkMessages = allMedia.filter(isLinkMessage);
  const mediaMessages = allMedia.filter((m) => !isLinkMessage(m));

  const tabs: { key: TabKey; label: string; icon: React.ReactNode; count: number }[] = [
    { key: "media", label: "Media", icon: <AppstoreOutlined />, count: mediaMessages.length },
    { key: "links", label: "Links", icon: <LinkOutlined />, count: linkMessages.length },
  ];

  return (
    <div className="mg">
      <div className="mg__tabs" role="tablist">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            type="button"
            role="tab"
            aria-selected={activeTab === tab.key}
            className={`mg__tab${activeTab === tab.key ? " mg__tab--active" : ""}`}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.icon}
            <span>{tab.label}</span>
            <span className="mg__tab-count">{tab.count}</span>
          </button>
        ))}
      </div>

      {activeTab === "media" && (
        <>
          {mediaMessages.length === 0 && !canUpload ? (
            <EmptyState
              icon={<PictureOutlined />}
              title="No media shared yet"
              hint="Photos, videos and files shared here will appear in this gallery."
            />
          ) : (
            <div className="mg__grid">
              {canUpload && (
                <>
                  <input
                    ref={inputRef}
                    multiple
                    type="file"
                    hidden
                    onChange={handleChange}
                  />
                  <div
                    className={`mg__upload${dragOver ? " mg__upload--drag" : ""}`}
                    role="button"
                    tabIndex={0}
                    onClick={() => inputRef.current?.click()}
                    onDragOver={(e) => {
                      e.preventDefault();
                      setDragOver(true);
                    }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={(e) => {
                      e.preventDefault();
                      setDragOver(false);
                      emitFiles(e.dataTransfer.files);
                    }}
                  >
                    <CloudUploadOutlined />
                    <span className="mg__upload-title">Upload</span>
                    <span className="mg__upload-hint">or drop files</span>
                  </div>
                </>
              )}
              {mediaMessages.map((message, index) => (
                <MediaGalleryItem
                  key={`${message.id}-${index}`}
                  message={message}
                />
              ))}
            </div>
          )}
        </>
      )}

      {activeTab === "links" && (
        <>
          {linkMessages.length > 0 ? (
            <div className="mg__links">
              {linkMessages.map((message, index) => (
                <LinkItem key={`${message.id}-${index}`} message={message} />
              ))}
            </div>
          ) : (
            <EmptyState
              icon={<LinkOutlined />}
              title="No links shared yet"
              hint="Links shared in this conversation will be collected here."
            />
          )}
        </>
      )}
    </div>
  );
};

export default MediaGallery;
